import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAllWatchlists,
  createWatchlist,
  addAktieToWatchlist,
} from 'modules/aktien/features/watchlistsSlice';
import { formatKurs, formatProz, makeComparator } from 'core/utils/format';
import SortHeader from 'core/components/SortHeader';

/**
 * Button mit Dropdown, um eine Aktie zu einer Watchlist hinzuzufügen
 * (oder schnell eine neue Liste anzulegen).
 */
function AddToWatchlistButton({ aktie }) {
  const dispatch = useDispatch();
  const watchlists = useSelector(selectAllWatchlists);
  const [open, setOpen] = useState(false);

  const payload = {
    isin: aktie.isin,
    aktienName: aktie.aktienName,
    indexName: aktie.indexName,
  };

  const handleAdd = (uuid) => {
    dispatch(addAktieToWatchlist({ uuid, aktie: payload }));
    setOpen(false);
  };

  const handleCreateAndAdd = async () => {
    const name = window.prompt('Name der neuen Liste:');
    if (!name || !name.trim()) return;
    try {
      const created = await dispatch(createWatchlist(name.trim())).unwrap();
      if (created?.uuid) {
        dispatch(addAktieToWatchlist({ uuid: created.uuid, aktie: payload }));
      }
    } catch {
      /* Fehler wird im Slice gehalten */
    }
    setOpen(false);
  };

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-md bg-ink px-2.5 py-1 text-xs font-medium text-paper transition-colors hover:bg-ink-soft"
      >
        + zu Liste
      </button>

      {open && (
        <>
          {/* Backdrop zum Schließen */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-lg border border-line bg-paper-soft shadow-xl">
            <div className="max-h-56 overflow-y-auto py-1">
              {watchlists.length === 0 ? (
                <p className="px-3 py-2 text-xs text-ink-muted">
                  Noch keine Liste vorhanden.
                </p>
              ) : (
                watchlists.map((w) => (
                  <button
                    key={w.uuid}
                    type="button"
                    onClick={() => handleAdd(w.uuid)}
                    className="block w-full truncate px-3 py-2 text-left text-sm text-ink-soft transition-colors hover:bg-ink/5"
                  >
                    {w.name}
                  </button>
                ))
              )}
            </div>
            <button
              type="button"
              onClick={handleCreateAndAdd}
              className="block w-full border-t border-line px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-ink/5"
            >
              ＋ Neue Liste…
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Tabelle der Aktien eines Index.
 *
 * @param {{ aktien: import('types/dto').AktieUebersichtDTO[] }} props
 */
/** Sortierwert einer Zeile je Spaltenschlüssel. */
const getValue = (a, key) => {
  if (key === 'aktienName') return a.aktienName;
  if (key === 'aktuellPreis') return a.aktuellPreis;
  if (key === 'aenderungProz') return a.aenderungProz;
  return null;
};

export default function AktienTabelle({ aktien }) {
  // Sortierung: keine aktive Spalte -> ursprüngliche Reihenfolge.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Zahlenspalten zuerst absteigend (größte Werte oben), Text aufsteigend.
      setSortDir(key === 'aktienName' ? 'asc' : 'desc');
    }
  };

  const sortierteAktien = useMemo(() => {
    if (!sortKey || !aktien) return aktien || [];
    return [...aktien].sort(makeComparator((row) => getValue(row, sortKey), sortDir));
  }, [aktien, sortKey, sortDir]);

  if (!aktien || aktien.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
        Keine Aktien gefunden.
      </p>
    );
  }

  const headerProps = { sortKey, sortDir, onSort: handleSort };

  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
            <SortHeader label="Name" columnKey="aktienName" {...headerProps} />
            <th className="hidden px-4 py-3 font-medium md:table-cell">ISIN</th>
            <SortHeader label="Kurs" columnKey="aktuellPreis" align="right" {...headerProps} />
            <SortHeader label="Änderung" columnKey="aenderungProz" align="right" {...headerProps} />
            <th className="px-4 py-3 text-right font-medium">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {sortierteAktien.map((a) => {
            const positiv = (a.aenderungProz ?? 0) >= 0;
            return (
              <tr
                key={a.isin}
                className="border-t border-line transition-colors hover:bg-ink/[0.03]"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-ink">{a.aktienName}</div>
                  {a.wkn && <div className="font-mono text-xs text-ink-muted">WKN {a.wkn}</div>}
                  {/* ISIN am Handy hier mitführen, da die eigene Spalte ausgeblendet ist. */}
                  <div className="font-mono text-xs text-ink-muted md:hidden">{a.isin}</div>
                </td>
                <td className="hidden px-4 py-3 font-mono text-xs text-ink-muted md:table-cell">{a.isin}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-ink">
                  {formatKurs(a.aktuellPreis, a.currency)}
                </td>
                <td
                  className={`px-4 py-3 text-right font-mono font-medium tabular-nums ${
                    positiv ? 'text-gain' : 'text-loss'
                  }`}
                >
                  {formatProz(a.aenderungProz)}
                </td>
                <td className="px-4 py-3 text-right">
                  <AddToWatchlistButton aktie={a} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
